// src/components/sections/LocationsSection.js
import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaClock, FaCar, FaSubway, FaWheelchair } from 'react-icons/fa';

const LocationsSection = () => {
    const locations = [
        {
            name: "Main Healthcare Campus",
            address: "123 Healthcare Street, Medical District",
            city: "New York, NY 10001",
            phone: "(212) 555-1234",
            hours: "24/7 Emergency, 8AM-8PM OPD",
            services: ["Full Services", "Emergency", "NICU", "Surgery"],
            distance: "Downtown",
            color: "#20B2AA"
        },
        {
            name: "Women's Health Center",
            address: "456 Wellness Avenue, Suite 300",
            city: "Brooklyn, NY 11201",
            phone: "(718) 555-5678",
            hours: "Mon-Fri: 8AM-6PM, Sat: 9AM-2PM",
            services: ["Gynecology", "Prenatal Care", "Fertility"],
            distance: "15 mins from subway",
            color: "#FF7F50"
        },
        {
            name: "Pediatric Care Center",
            address: "789 Childcare Boulevard",
            city: "Queens, NY 11355",
            phone: "(347) 555-9012",
            hours: "Mon-Sun: 8AM-8PM",
            services: ["Pediatrics", "Vaccinations", "Development"],
            distance: "Near Q65 bus route",
            color: "#87CEEB"
        }
    ];

    const emergencyContacts = [
        { label: "Medical Emergency", number: "(212) 555-EMER", icon: <FaPhone /> },
        { label: "Ambulance Service", number: "(212) 555-AMBUL", icon: <FaCar /> },
        { label: "Poison Control", number: "(800) 222-1222", icon: <FaWheelchair /> }
    ];

    return (
        <section id="locations" className="section-padding" style={{ backgroundColor: 'var(--light-bg)' }}>
            <Container>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-5"
                >
                    <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                        Our Locations & Reach
                    </h2>
                    <p className="lead" style={{ color: '#666', maxWidth: '700px', margin: '0 auto' }}>
                        Accessible healthcare centers across the city for your convenience
                    </p>
                </motion.div>

                <Row className="g-4 mb-5">
                    {locations.map((location, index) => (
                        <Col lg={4} md={6} key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <Card className="healthcare-card border-0 h-100">
                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-start mb-4">
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: '60px',
                                                    height: '60px',
                                                    backgroundColor: `${location.color}20`,
                                                    color: location.color,
                                                    fontSize: '1.5rem',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <FaMapMarkerAlt />
                                            </div>
                                            <div>
                                                <Card.Title className="fw-bold mb-1" style={{ color: 'var(--dark-teal)' }}>
                                                    {location.name}
                                                </Card.Title>
                                                <small className="text-muted">{location.distance}</small>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <div className="d-flex align-items-start mb-3">
                                                <FaMapMarkerAlt className="me-3 mt-1" style={{ color: location.color, width: '16px' }} />
                                                <div>
                                                    <p className="mb-1" style={{ color: '#666' }}>{location.address}</p>
                                                    <p className="mb-0" style={{ color: '#666' }}>{location.city}</p>
                                                </div>
                                            </div>

                                            <div className="d-flex align-items-center mb-3">
                                                <FaPhone className="me-3" style={{ color: location.color, width: '16px' }} />
                                                <span style={{ color: '#666' }}>{location.phone}</span>
                                            </div>

                                            <div className="d-flex align-items-start mb-4">
                                                <FaClock className="me-3 mt-1" style={{ color: location.color, width: '16px' }} />
                                                <span style={{ color: '#666' }}>{location.hours}</span>
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <h6 className="fw-bold mb-3" style={{ color: location.color }}>
                                                Services Available:
                                            </h6>
                                            <div className="d-flex flex-wrap gap-2">
                                                {location.services.map((service, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="badge"
                                                        style={{
                                                            backgroundColor: `${location.color}20`,
                                                            color: location.color,
                                                            fontWeight: 'normal',
                                                            padding: '6px 12px'
                                                        }}
                                                    >
                                                        {service}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>

                                        <Button
                                            variant="outline-primary"
                                            className="w-100"
                                            style={{
                                                borderColor: location.color,
                                                color: location.color,
                                                borderRadius: '50px'
                                            }}
                                        >
                                            <FaMapMarkerAlt className="me-2" />
                                            Get Directions
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    ))}
                </Row>

                <Row className="g-4 align-items-center">
                    <Col lg={6}>
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            viewport={{ once: true }}
                        >
                            <div className="p-4 rounded healthcare-card">
                                <h4 className="fw-bold mb-4" style={{ color: 'var(--dark-teal)' }}>
                                    Find Your Nearest Center
                                </h4>

                                <div className="mb-4">
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control form-control-lg"
                                            placeholder="Enter your ZIP code or address"
                                            style={{ 
                                                borderRadius: '30px 0 0 30px',
                                                fontSize: '0.9rem' // REDUCED FONT SIZE
                                            }}
                                        />
                                        <button
                                            className="btn btn-primary-teal"
                                            style={{ borderRadius: '0 50px 50px 0' }}
                                        >
                                            Search
                                        </button>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center p-3 rounded" style={{ backgroundColor: 'var(--teal-light)' }}>
                                            <FaSubway className="me-3" size={24} style={{ color: 'var(--primary-teal)' }} />
                                            <div>
                                                <h6 className="fw-bold mb-1">Public Transport</h6>
                                                <small className="text-muted">All locations accessible via subway/bus</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center p-3 rounded" style={{ backgroundColor: 'var(--teal-light)' }}>
                                            <FaCar className="me-3" size={24} style={{ color: 'var(--primary-teal)' }} />
                                            <div>
                                                <h6 className="fw-bold mb-1">Parking Available</h6>
                                                <small className="text-muted">Secure parking at all locations</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </Col>

                    <Col lg={6}>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <div
                                className="p-5 rounded text-white"
                                style={{
                                    background: 'linear-gradient(135deg, var(--coral) 0%, #FF6347 100%)',
                                    height: '100%'
                                }}
                            >
                                <h4 className="fw-bold mb-4">Emergency Contacts</h4>
                                <p className="mb-4 opacity-90">
                                    Save these numbers for immediate assistance. Our emergency teams are ready 24/7.
                                </p>

                                {emergencyContacts.map((contact, index) => (
                                    <div key={index} className="mb-3 p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <div className="me-3">
                                                    {contact.icon}
                                                </div>
                                                <div>
                                                    <h6 className="fw-bold mb-1">{contact.label}</h6>
                                                    <p className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                        {contact.number}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline-light"
                                                size="sm"
                                                style={{ borderRadius: '20px' }}
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                <div className="mt-4 pt-3 border-top border-white border-opacity-25">
                                    <small className="opacity-75">
                                        <FaClock className="me-2" />
                                        All emergency lines are monitored 24 hours a day, 7 days a week
                                    </small>
                                </div>
                            </div>
                        </motion.div>
                    </Col>
                </Row>

                {/* Virtual Tour Callout */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-5 text-center"
                >
                    <div className="p-4 rounded" style={{ backgroundColor: 'var(--soft-yellow)' }}>
                        <h4 className="fw-bold mb-3" style={{ color: 'var(--dark-teal)' }}>
                            Can't Visit in Person?
                        </h4>
                        <p className="mb-4" style={{ maxWidth: '600px', margin: '0 auto', color: '#666' }}>
                            Take a virtual tour of our facilities or schedule a video consultation with our specialists
                        </p>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                            <Button className="btn-primary-teal px-4">
                                Virtual Tour
                            </Button>
                            <Button className="btn-sky-blue px-4">
                                Video Consultation
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </Container>
        </section>
    );
};

export default LocationsSection;